import React, { useEffect, useRef } from 'react';
import {
	useDaily,
	useParticipantIds,
	useParticipant,
} from '@daily-co/daily-react-hooks';
import { alpha, Box, IconButton, Stack } from '@mui/material';
import { IPreviewProps } from './types';
import { theme } from '../../../../../common/styles/theme';
import { CloseIcon } from '../../../../../../icons/CloseIcon';
import { useUIState } from '../../shared/contexts/UIStateProvider';
import { useScreenShare } from '../../shared/contexts/ScreenShareProvider';
import { usePip } from '../../shared/contexts/PipProvider';
import { LeaveModal, LEAVE_MODAL } from '../../Call/LeaveModal';
import { Audio } from '../../shared/components/Audio/Audio';
import { PreviewActions } from './PreviewActions';
import { PreviewStrip } from './PreviewStrip';
import { PREVIEW_POSITION_KEY, useDragPosition } from './useDragPosition';

export function Preview ({
	isToken,
	startedAt,
	onLeave,
	onOpen,
}: IPreviewProps) {
	const className = 'preview';

	const callObject = useDaily();
	const participantIds = useParticipantIds({ sort: 'joined_at' });
	const { screens } = useScreenShare();
	const { isPipActive } = usePip();
	const screenSharerId = screens[0]?.session_id || null;
	const { openModal } = useUIState() as any;
	const drag = useDragPosition(PREVIEW_POSITION_KEY);

	const tileIds = screenSharerId
		? [`${screenSharerId}::screen`, ...participantIds]
		: participantIds;

	const onLeaveHandle = async () => {
		if (isToken) {
			openModal(LEAVE_MODAL);
			return;
		}
		onLeave();
		await callObject?.leave();
		callObject?.destroy();
	};

	// The call page's <Audio /> (Call/Container) is unmounted here, so the preview plays the others
	// itself — in both views, the PiP window carries no sound.
	const common = <>
		<LeaveModal />
		<Audio />
	</>;

	// the PiP window already shows everyone: keep only a thin strip, so the lesson page stays visible
	if (isPipActive) {
		return (<>
			{common}
			<PreviewStrip startedAt={startedAt} onBack={onOpen} />
		</>);
	}

	return (<>
		{common}
		<Stack
			className={className}
			ref={drag.ref}
			gap={.5}
			sx={{
				position: 'fixed',
				bottom: theme.spacing(5),
				right: theme.spacing(5),
				background: theme.palette.grey[900],
				borderRadius: theme.spacing(.75),
				overflow: 'hidden',
				zIndex: 1000,
				cursor: drag.dragging ? 'grabbing' : 'pointer',
				touchAction: 'none',
				userSelect: 'none',
				p: .5,
				...drag.positionSx,
			}}
			{...drag.handlers}
			onClick={() => { if (!drag.consumeDragClick()) onOpen(); }}
		>
			{tileIds.map((id) => (
				<PreviewTile key={id} id={id} />
			))}
			<PreviewActions onBack={onOpen} />
			<IconButton
				className={`${className}__close`}
				onClick={(e) => { e.stopPropagation(); onLeaveHandle(); }}
				color={'primary'}
				sx={{
					position: 'absolute',
					zIndex: 100,
					top: 0,
					right: 0,
					color: alpha(theme.palette.common.white, .8),
					borderRadius: '50%',

					'&:hover, &:active, &:focus': {
						color: theme.palette.common.white,
					},
					'.MuiSvgIcon-root': { width: theme.spacing(3), height: theme.spacing(3) }
				}}
			>
				<CloseIcon />
			</IconButton>
		</Stack>
	</>);
};

const PreviewTile: React.FC<{ id: string }> = ({ id }) => {
	const [sessionId, kind] = id.split('::');
	const isScreen = kind === 'screen';
	const p = useParticipant(sessionId);
	const vRef = useRef<HTMLVideoElement | null>(null);

	// The participant object is replaced on every participant-updated event,
	// so depend on the tracks themselves to avoid re-attaching (and flickering) the same media.
	// Video only: sound comes from the preview's <Audio />, so it's there even without tiles.
	const source = isScreen ? p?.tracks.screenVideo : p?.tracks.video;
	const videoTrack = source?.state === 'playable' ? source.persistentTrack || null : null;

	useEffect(() => {
		const videoEl = vRef.current;
		if (!videoEl) return;

		if (!videoTrack) {
			videoEl.srcObject = null;
			return;
		}

		const current = videoEl.srcObject as MediaStream | null;
		if ((current?.getVideoTracks()[0]?.id || null) === videoTrack.id) return;

		const stream = new MediaStream();
		stream.addTrack(videoTrack);
		videoEl.srcObject = stream;
		videoEl.play().catch(() => { });
	}, [videoTrack, videoTrack?.id]);

	return (
		<Box
			sx={{
				position: 'relative',
				flex: '0 0 auto',
				minWidth: theme.spacing(20),
				width: '20vw',
				aspectRatio: 16/9,
				borderRadius: theme.spacing(.5),
				overflow: 'hidden',
				background: theme.palette.grey[900],
			}}
		>
			<Box
				component={'video'}
				ref={vRef}
				autoPlay
				playsInline
				muted
				sx={{
					width: 1,
					height: 1,
					objectFit: isScreen ? 'contain' : 'cover',
					transform: (p?.local && !isScreen) ? 'scaleX(-1)' : 'none',
				}}
			/>
			<Box
				sx={{
					position: 'absolute',
					left: 0,
					right: 0,
					bottom: 0,
					p: theme.spacing(1),
					whiteSpace: 'nowrap',
					color: theme.palette.common.white,
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					textShadow: `0 0 8px ${theme.palette.grey[900]}`,
				}}
			>
				{p?.user_name || p?.session_id || 'unknown'}
			</Box>
		</Box>
	);
};
